import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Megaphone, Plus, Calendar, Mail, MessageCircle, Users,
  Loader2, Send, Clock, CheckCircle, XCircle, Eye, Edit, Trash2,
  Target, TrendingUp, BarChart3
} from 'lucide-react';
import api from '../lib/api';

const Campanhas = () => {
  const [campanhas, setCampanhas] = useState([
    {
      id: '1',
      nome: 'Matrícula 2027',
      descricao: 'Campanha de matrículas para o próximo ano letivo',
      status: 'ativa',
      tipo: 'email',
      data_inicio: '2026-10-01',
      data_fim: '2026-12-15',
      enviados: 245,
      abertos: 189,
      cliques: 67,
    },
    {
      id: '2',
      nome: 'Festa Junina',
      descricao: 'Convite para a festa junina da escola',
      status: 'concluida',
      tipo: 'ambos',
      data_inicio: '2026-06-01',
      data_fim: '2026-06-20',
      enviados: 658,
      abertos: 521,
      cliques: 234,
    },
  ]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    tipo: 'email',
    mensagem: '',
  });

  const getStatusBadge = (status) => {
    const config = {
      ativa: { label: 'Ativa', class: 'bg-green-100 text-green-800' },
      pausada: { label: 'Pausada', class: 'bg-yellow-100 text-yellow-800' },
      concluida: { label: 'Concluída', class: 'bg-gray-100 text-gray-800' },
      rascunho: { label: 'Rascunho', class: 'bg-blue-100 text-blue-800' },
    };
    return config[status] || config.rascunho;
  };

  const getTipoBadge = (tipo) => {
    const config = {
      email: { label: 'E-mail', icon: Mail, class: 'bg-blue-100 text-blue-800' },
      whatsapp: { label: 'WhatsApp', icon: MessageCircle, class: 'bg-green-100 text-green-800' },
      ambos: { label: 'E-mail + WhatsApp', icon: Send, class: 'bg-purple-100 text-purple-800' },
    };
    return config[tipo] || config.email;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Megaphone className="w-7 h-7 text-pink-500" />
            Campanhas de Marketing
          </h1>
          <p className="text-muted-foreground mt-1">
            Gerencie suas campanhas de comunicação e marketing
          </p>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-pink-600 hover:bg-pink-700">
          <Plus className="w-4 h-4 mr-2" />
          Nova Campanha
        </Button>
      </div>

      {/* Métricas Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-pink-100 rounded-lg">
              <Megaphone className="w-6 h-6 text-pink-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{campanhas.length}</p>
              <p className="text-sm text-muted-foreground">Campanhas</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Send className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{campanhas.reduce((a, c) => a + c.enviados, 0)}</p>
              <p className="text-sm text-muted-foreground">Enviados</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Eye className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{campanhas.reduce((a, c) => a + c.abertos, 0)}</p>
              <p className="text-sm text-muted-foreground">Abertos</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Target className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{campanhas.reduce((a, c) => a + c.cliques, 0)}</p>
              <p className="text-sm text-muted-foreground">Cliques</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Campanhas */}
      <div className="grid gap-4">
        {campanhas.map(campanha => {
          const statusConfig = getStatusBadge(campanha.status);
          const tipoConfig = getTipoBadge(campanha.tipo);
          const TipoIcon = tipoConfig.icon;
          
          return (
            <Card key={campanha.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-lg">{campanha.nome}</h3>
                      <Badge className={statusConfig.class}>{statusConfig.label}</Badge>
                      <Badge className={tipoConfig.class}>
                        <TipoIcon className="w-3 h-3 mr-1" />
                        {tipoConfig.label}
                      </Badge>
                    </div>
                    <p className="text-muted-foreground text-sm">{campanha.descricao}</p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {campanha.data_inicio} a {campanha.data_fim}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm">
                      <Eye className="w-4 h-4 mr-1" />
                      Ver
                    </Button>
                    <Button variant="outline" size="sm">
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Métricas da Campanha */}
                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t">
                  <div className="text-center">
                    <p className="text-xl font-bold text-blue-600">{campanha.enviados}</p>
                    <p className="text-xs text-muted-foreground">Enviados</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-green-600">{campanha.abertos}</p>
                    <p className="text-xs text-muted-foreground">Abertos ({Math.round(campanha.abertos/campanha.enviados*100)}%)</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-purple-600">{campanha.cliques}</p>
                    <p className="text-xs text-muted-foreground">Cliques ({Math.round(campanha.cliques/campanha.enviados*100)}%)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Modal Nova Campanha */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nova Campanha</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Nome da Campanha</label>
              <Input 
                placeholder="Ex: Matrícula 2027"
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Descrição</label>
              <Textarea 
                placeholder="Descreva o objetivo da campanha..."
                value={formData.descricao}
                onChange={(e) => setFormData({...formData, descricao: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Canal de Envio</label>
              <div className="flex gap-2">
                {['email', 'whatsapp', 'ambos'].map(tipo => (
                  <Button
                    key={tipo}
                    variant={formData.tipo === tipo ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFormData({...formData, tipo})}
                  >
                    {tipo === 'email' && <Mail className="w-4 h-4 mr-1" />}
                    {tipo === 'whatsapp' && <MessageCircle className="w-4 h-4 mr-1" />}
                    {tipo === 'ambos' && <Send className="w-4 h-4 mr-1" />}
                    {tipo === 'email' ? 'E-mail' : tipo === 'whatsapp' ? 'WhatsApp' : 'Ambos'}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button className="bg-pink-600 hover:bg-pink-700">Criar Campanha</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Campanhas;
